import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Form, Button, Input } from "antd";
import { MailOutlined, KeyOutlined } from "@ant-design/icons";
import { NotificationManager } from "react-notifications";
import KnackLogo from "../../assets/images/knackLogo.png";
import loginService from "../../services/login-service"; // Ensure this service contains ValidateToken API

const ForgotPass = () => {
  const [form] = Form.useForm();
  const [step, setStep] = useState("email"); // Steps: email, reset, expired
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get("token");
    if (tokenParam) {
      const decodedToken = encodeURIComponent(tokenParam);
      console.log(decodedToken);
      setToken(decodedToken);
      validateToken(decodedToken);
    }
  }, [location.search]);

  const validateToken = async (token) => {
    try {
      setLoading(true);

      const response = await loginService.ValidateToken(token);

      if (response.data.statusCode === 200) {
        setStep("reset");
      } else if (response.data.statusCode === 400) {
        setStep("expired");
        NotificationManager.error(response.data.message);
      }
    } catch (err) {
      setStep("expired");
      if (err.response && err.response.data && err.response.data.message) {
        NotificationManager.error(err.response.data.message);
      } else {
        NotificationManager.error(
          "An error occurred while validating the token."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Send reset email
  const handleSendResetEmail = async (values) => {
    debugger;
    try {
      setLoading(true);
      const response = await loginService.SendResetEmail({
        email: values.email,
      });
      if (response.data.statusCode === 200) {
        NotificationManager.success("Password reset link sent to your email!");
        form.resetFields();
      } else {
        NotificationManager.error(response.data.message);
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        NotificationManager.error(error.response.data.message);
      } else {
        NotificationManager.error("Failed to send reset link");
      }
    } finally {
      setLoading(false);
    }
  };

  // Submit new password
  const handleResetPasswordSubmit = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      NotificationManager.error("Passwords do not match!");
      return;
    }
    try {
      setLoading(true);
      const inputData = {
        token: token,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      };

      const response = await loginService.ForgotPassword(inputData);

      if (response.data.statusCode === 200) {
        NotificationManager.success("Password reset successful!");
        navigate("/");
      } else {
        NotificationManager.error(response.data.message);
      }
    } catch (error) {
      NotificationManager.error("Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-content content login_pg">
      <div className="content-wrapper">
        <div className="content-body">
          <section className="flexbox-container">
            <div className="col-12 d-flex align-items-center justify-content-center">
              <div className="col-md-4 col-10 box-shadow-2 p-0 login-block">
                <div className="card border-grey border-lighten-3 px-3 pt-30 pb-2 m-0">
                  <div className="kanck_logo mb-3 text-center">
                    <img
                      src={KnackLogo}
                      alt="logo"
                      className="mt-2"
                      style={{ width: "20%", marginBottom: "10px" }}
                    />
                    <h4>
                      {step === "email"
                        ? "Forgot Password"
                        : step === "reset"
                        ? "Change Your Password"
                        : "Link Expired"}
                    </h4>
                    <p
                      className="text-center pt-2"
                      style={{
                        fontSize: "16px",
                        color: "#555",
                        width: "80%",
                        textAlign: "center",
                        margin: "0 auto",
                      }}
                    >
                      {step === "email" &&
                        "Enter your registered email address to receive a password reset link."}
                      {step === "reset" &&
                        "Enter your new password below to reset your account password."}
                      {step === "expired" &&
                        "This password reset link is invalid or has expired. Please request a new one."}
                    </p>
                  </div>
                  <div className="card-body pt-0">
                    {/* ---------------- EMAIL STEP ---------------- */}
                    {step === "email" && (
                      <Form
                        form={form}
                        onFinish={handleSendResetEmail}
                        layout="vertical"
                        className="mt-2  login_form"
                      >
                        <Form.Item
                          name="email"
                          rules={[
                            {
                              required: true,
                              message: "Please enter your email",
                            },
                            {
                              type: "email",
                              message: "Please enter a valid email",
                            },
                          ]}
                          style={{ width: "100%" }}
                        >
                          <Input
                            prefix={<MailOutlined />}
                            size="large"
                            placeholder="Enter your Email"
                          />
                        </Form.Item>

                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={loading}
                          className="login_btn w-100 mb-2 mt-2"
                        >
                          Send Reset Link
                        </Button>
                      </Form>
                    )}

                    {/* ---------------- RESET STEP ---------------- */}
                    {step === "reset" && (
                      <Form
                        onFinish={handleResetPasswordSubmit}
                        layout="vertical"
                        className="mt-2  login_form"
                      >
                        <Form.Item
                          name="newPassword"
                          rules={[
                            { required: true, message: "Enter new password" },
                            {
                              pattern:
                                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                              message:
                                "Password must be at least 8 characters long, include uppercase, lowercase, number, and special character",
                            },
                          ]}
                          style={{ width: "100%" }}
                        >
                          <Input.Password
                            prefix={<KeyOutlined />}
                            size="large"
                            placeholder="New Password"
                          />
                        </Form.Item>

                        <Form.Item
                          name="confirmPassword"
                          dependencies={["newPassword"]}
                          rules={[
                            { required: true, message: "Confirm password" },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (
                                  !value ||
                                  getFieldValue("newPassword") === value
                                ) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(
                                  new Error("Passwords do not match")
                                );
                              },
                            }),
                          ]}
                          style={{ width: "100%" }}
                        >
                          <Input.Password
                            prefix={<KeyOutlined />}
                            size="large"
                            placeholder="Confirm Password"
                          />
                        </Form.Item>

                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={loading}
                          className="login_btn w-100 mb-2"
                        >
                          Reset Password
                        </Button>
                      </Form>
                    )}

                    {/* ---------------- EXPIRED STEP ---------------- */}
                    {step === "expired" && (
                      <Button
                        type="primary"
                        className="login_btn w-100 mb-2"
                        onClick={() => setStep("email")}
                      >
                        Request New Link
                      </Button>
                    )}
                    <div className="d-flex justify-content-between align-items-center">
                      <div></div>
                      <Button
                        type="link"
                        className="p-0 forgot-password-link"
                        onClick={() => navigate("/")}
                        style={{
                          color: "#1890ff",
                          cursor: "pointer",
                        }}
                      >
                        Back to Login ?
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ForgotPass;
